import { parseCalculatorUserOverlayDocumentV4 } from "./calculator-user-layer-v4-contract.js";
import { browserPresetWorkspace } from "./presets/browser-workspace.js";

export const CALCULATOR_USER_LAYER_PRESET_COLLECTION_KEY = "calculator-overlays-v4";
export const CALCULATOR_USER_LAYER_PRESET_FACADE_NAME =
  "__fishystuffCalculatorUserLayerV4Presets";

function isPromiseLike(value) {
  return Boolean(value)
    && (typeof value === "object" || typeof value === "function")
    && typeof value.then === "function";
}

function payloadsEqual(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function operationId(revision) {
  const cryptoRef = globalThis.crypto;
  if (cryptoRef && typeof cryptoRef.randomUUID === "function") {
    return `calculator_overlay_${cryptoRef.randomUUID()}`;
  }
  return `calculator_overlay_${Date.now().toString(36)}_${revision.toString(36)}`;
}

export class CalculatorUserLayerV4PresetBinding {
  constructor(options = {}) {
    if (typeof options.activateDocument !== "function") {
      throw new TypeError(
        "Calculator user-layer preset activation requires a synchronous callback",
      );
    }
    this._workspace = options.workspace ?? browserPresetWorkspace();
    this._activateDocumentCallback = options.activateDocument;
    this._defaultDocument = parseCalculatorUserOverlayDocumentV4(
      options.initialDocument,
    );
    this._currentDocument = parseCalculatorUserOverlayDocumentV4(
      options.currentDocument ?? this._defaultDocument,
    );
    this._bound = false;
    this._activationDepth = 0;
    this._documentRevision = 0;
    this._editQueue = Promise.resolve();
    this._unbindRuntime = null;
    this.ready = Promise.resolve(null);
  }

  get collectionKey() {
    return CALCULATOR_USER_LAYER_PRESET_COLLECTION_KEY;
  }

  bind() {
    if (this._bound) {
      return this;
    }
    if (
      !this._workspace
      || typeof this._workspace.bindCollectionRuntime !== "function"
      || typeof this._workspace.defineFixedPreset !== "function"
      || typeof this._workspace.edit !== "function"
    ) {
      throw new Error("Calculator user-layer presets require the typed preset workspace");
    }
    this._unbindRuntime = this._workspace.bindCollectionRuntime(this.collectionKey, {
      captureCurrent: () => this.captureCurrentDocument(),
      applyCurrent: (document) => this.activateDocument(document),
    });
    this._bound = true;
    this.ready = this._initializeWorkspace().catch((error) => {
      this.dispose();
      throw error;
    });
    return this;
  }

  async _initializeWorkspace() {
    await this._workspace.defineFixedPreset(this.collectionKey, {
      id: "default",
      name: "Default",
      payload: this._defaultDocument,
    });
    let state = await this._workspace.load(this.collectionKey);
    if (state.kind === "missing") {
      const usesDefault = payloadsEqual(this._currentDocument, this._defaultDocument);
      state = await this._workspace.initialize(
        this.collectionKey,
        this._currentDocument,
        {
          expectedRevision: state.revision,
          source: usesDefault
            ? { kind: "fixed", id: "default" }
            : { kind: "none", id: "" },
        },
      );
    }
    if (state.kind !== "ready") {
      throw new Error(`Calculator overlay preset workspace is ${state.kind}`);
    }
    return state;
  }

  captureCurrentDocument() {
    return parseCalculatorUserOverlayDocumentV4(this._currentDocument);
  }

  updateCurrentDocument(document, options = {}) {
    const normalized = parseCalculatorUserOverlayDocumentV4(document);
    this._currentDocument = normalized;
    this._documentRevision += 1;
    if (options.track !== false && this._activationDepth === 0) {
      const revision = this._documentRevision;
      const actionId = typeof options.actionId === "string" && options.actionId
        ? options.actionId
        : operationId(revision);
      this._editQueue = this._editQueue
        .catch(() => null)
        .then(async () => {
          await this.ready;
          const latest = this._workspace.state(this.collectionKey);
          if (latest.kind !== "ready" || revision < this._documentRevision) {
            return latest;
          }
          return this._workspace.edit(this.collectionKey, normalized, {
            expectedRevision: latest.revision,
            actionId,
            actionKind: "overlay-edit",
          });
        });
    }
    return this.captureCurrentDocument();
  }

  activateDocument(document) {
    const normalized = parseCalculatorUserOverlayDocumentV4(document);
    const previousDocument = this._currentDocument;
    const previousRevision = this._documentRevision;
    this._activationDepth += 1;
    try {
      const result = this._activateDocumentCallback(normalized, {
        collectionKey: this.collectionKey,
      });
      if (isPromiseLike(result)) {
        throw new TypeError(
          "Calculator user-layer preset activation callback must be synchronous",
        );
      }
      if (this._documentRevision === previousRevision) {
        this._currentDocument = normalized;
        this._documentRevision += 1;
      }
      return this.captureCurrentDocument();
    } catch (error) {
      this._currentDocument = previousDocument;
      this._documentRevision = previousRevision;
      throw error;
    } finally {
      this._activationDepth -= 1;
    }
  }

  dispose() {
    this._unbindRuntime?.();
    this._unbindRuntime = null;
    this._bound = false;
  }
}

export function createCalculatorUserLayerV4PresetBinding(options = {}) {
  return new CalculatorUserLayerV4PresetBinding(options).bind();
}

export function createCalculatorUserLayerV4PresetFacade() {
  let binding = null;
  return Object.freeze({
    collectionKey: CALCULATOR_USER_LAYER_PRESET_COLLECTION_KEY,
    isBound() {
      return binding !== null;
    },
    bind(options = {}) {
      if (binding) {
        throw new Error("Calculator user-layer preset facade is already bound");
      }
      const candidate = new CalculatorUserLayerV4PresetBinding(options);
      binding = candidate;
      try {
        const bound = candidate.bind();
        bound.ready = bound.ready.catch((error) => {
          if (binding === candidate) {
            binding = null;
          }
          throw error;
        });
        return bound;
      } catch (error) {
        if (binding === candidate) {
          binding = null;
        }
        candidate.dispose();
        throw error;
      }
    },
    captureCurrentDocument() {
      return binding ? binding.captureCurrentDocument() : null;
    },
    updateCurrentDocument(document, options = {}) {
      if (!binding) {
        throw new Error("Calculator user-layer preset facade is not bound");
      }
      return binding.updateCurrentDocument(document, options);
    },
    reconcileCurrentDocument(document, options = {}) {
      if (!binding) {
        throw new Error("Calculator user-layer preset facade is not bound");
      }
      return binding.updateCurrentDocument(document, options);
    },
  });
}

export function installCalculatorUserLayerV4PresetFacade(
  target = globalThis.window ?? null,
) {
  if (!target) {
    return null;
  }
  const existing = target[CALCULATOR_USER_LAYER_PRESET_FACADE_NAME];
  if (existing) {
    return existing;
  }
  const facade = createCalculatorUserLayerV4PresetFacade();
  target[CALCULATOR_USER_LAYER_PRESET_FACADE_NAME] = facade;
  return facade;
}

if (globalThis.window) {
  installCalculatorUserLayerV4PresetFacade(globalThis.window);
}
